/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { createContext, useContext, useState, useCallback } from "react";
import PropTypes from "prop-types";

import {
  clearStoredAuth,
  getStoredUser,
  loginRequest,
  getStoredAccessToken,
} from "api/client";

const AuthContext = createContext(null);
AuthContext.displayName = "AuthContext";

function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getStoredAccessToken());

  const login = useCallback(async (email, password) => {
    const data = await loginRequest(email, password);
    setUser(data.user ?? null);
    setIsAuthenticated(true);
    return data;
  }, []);

  const logout = useCallback(() => {
    clearStoredAuth();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AuthProvider, useAuth };
